/**
 * Storage Utilities
 *
 * Handles token and data storage with sessionStorage (for auth)
 * and localStorage (for preferences like theme).
 *
 * Auth tokens use sessionStorage to ensure session clears on browser close.
 * User preferences use localStorage to persist across sessions.
 */

// Storage keys
const STORAGE_KEYS = {
  TOKEN: 'token',
  REFRESH_TOKEN: 'refreshToken',
  THEME: 'theme',
  SIDEBAR_EXPANDED: 'sidebar-expanded',
} as const;

// ============================================
// Session Storage (Auth - clears on browser close)
// ============================================

/**
 * Get the stored authentication token
 */
export function getStoredToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.TOKEN);
  } catch {
    return null;
  }
}

/**
 * Store the authentication token
 */
export function setStoredToken(token: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEYS.TOKEN, token);
  } catch (error) {
    console.error('Failed to store token:', error);
  }
}

/**
 * Get the stored refresh token
 */
export function getStoredRefreshToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  } catch {
    return null;
  }
}

/**
 * Store the refresh token
 */
export function setStoredRefreshToken(token: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  } catch (error) {
    console.error('Failed to store refresh token:', error);
  }
}

/**
 * Clear all authentication-related storage
 */
export function clearAuthStorage(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  } catch (error) {
    console.error('Failed to clear auth storage:', error);
  }
}

/**
 * Clear all session storage
 */
export function clearSessionStorage(): void {
  try {
    sessionStorage.clear();
  } catch (error) {
    console.error('Failed to clear session storage:', error);
  }
}

// ============================================
// Local Storage (Preferences - persists across sessions)
// ============================================

/**
 * Get the stored theme preference
 */
export function getStoredTheme(): 'light' | 'dark' {
  try {
    const theme = localStorage.getItem(STORAGE_KEYS.THEME);
    if (theme === 'dark' || theme === 'light') {
      return theme;
    }
    // Check system preference
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  } catch {
    return 'light';
  }
}

/**
 * Store the theme preference
 */
export function setStoredTheme(theme: 'light' | 'dark'): void {
  try {
    localStorage.setItem(STORAGE_KEYS.THEME, theme);
  } catch (error) {
    console.error('Failed to store theme:', error);
  }
}

/**
 * Get the sidebar expanded state
 */
export function getSidebarExpanded(): boolean {
  try {
    const value = localStorage.getItem(STORAGE_KEYS.SIDEBAR_EXPANDED);
    return value !== 'false'; // Default to true
  } catch {
    return true;
  }
}

/**
 * Set the sidebar expanded state
 */
export function setSidebarExpanded(expanded: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEYS.SIDEBAR_EXPANDED, String(expanded));
  } catch (error) {
    console.error('Failed to store sidebar state:', error);
  }
}

// ============================================
// Generic Storage Helpers
// ============================================

/**
 * Get an item from localStorage with JSON parsing
 */
export function getLocalStorageItem<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item) as T;
  } catch {
    return defaultValue;
  }
}

/**
 * Set an item in localStorage with JSON serialization
 */
export function setLocalStorageItem<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to store ${key}:`, error);
  }
}

/**
 * Remove an item from localStorage
 */
export function removeLocalStorageItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Failed to remove ${key}:`, error);
  }
}

/**
 * Get an item from sessionStorage with JSON parsing
 */
export function getSessionStorageItem<T>(key: string, defaultValue: T): T {
  try {
    const item = sessionStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item) as T;
  } catch {
    return defaultValue;
  }
}

/**
 * Set an item in sessionStorage with JSON serialization
 */
export function setSessionStorageItem<T>(key: string, value: T): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to store ${key}:`, error);
  }
}
